// AppShell — guscio applicativo: sidebar di navigazione + topbar di sezione.
// Avvolge tutte le pagine "gestionali" (non le viste pubbliche/OBS/login).
// Solo presentazione: nessuna logica di dominio vive qui.
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from './icons';
import logoNexter from '../img/logonexter.png';
import logoRaceSense from '../img/logo-racesense-live.svg';
import logoRaceSenseMark from '../img/logo-racesense-mark.svg';
import { clearSession, useSession, canUse, featureEnabled } from '../session';
import { ThemeToggle } from './ThemeContext';
import './AppShell.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const SIDEBAR_KEY = 'rs_sidebar_collapsed';
const APP_VERSION = 'v4.2';

export const NAV = [
  {
    group: 'Sessioni', items: [
      { to: '/race', label: 'Gara', kicker: 'Sessione · Gara', desc: 'Setup, regia e classifica live', icon: Icon.Play, accent: 'red', feature: 'race' },
      { to: '/allenamento', label: 'Allenamento', kicker: 'Sessione · Allenamento', desc: 'Test piloti e telemetria', icon: Icon.Activity, accent: 'lime', feature: 'training' },
    ],
  },
  {
    group: 'Gestione', items: [
      { to: '/circuiti', label: 'Circuiti', kicker: 'Gestione', desc: 'Tracciati GPS, settori, box', icon: Icon.Track },
      { to: '/piloti', label: 'Partecipanti', kicker: 'Gestione', desc: 'Piloti e team', icon: Icon.Users },
      { to: '/auto', label: 'Auto', kicker: 'Gestione', desc: 'Auto e dati tecnici per l\'allenamento', icon: Icon.Car, feature: 'training' },
      { to: '/campionati', label: 'Campionati', kicker: 'Gestione', desc: 'Competizioni e formule', icon: Icon.Trophy },
      { to: '/sponsor', label: 'Sponsor', kicker: 'Gestione', desc: 'Loghi partner e overlay OBS', icon: Icon.Star },
      { to: '/landing-gara', label: 'Landing gara', kicker: 'Gestione', desc: 'Pagina pubblica e countdown', icon: Icon.Monitor, feature: 'race' },
    ],
  },
  {
    group: 'Archivio', items: [
      { to: '/gare-passate', label: 'Gare passate', kicker: 'Archivio', desc: 'Replay, classifiche, analisi', icon: Icon.Clock, feature: 'race' },
      { to: '/allenamenti-passati', label: 'Allenamenti passati', kicker: 'Archivio', desc: 'Sessioni di test salvate', icon: Icon.Archive, feature: 'training' },
    ],
  },
];

// Voci riservate all'amministratore della nostra copia.
const ADMIN_NAV = {
  group: 'Amministrazione', items: [
    { to: '/clienti', label: 'Clienti', kicker: 'Amministrazione', desc: 'Copie dei clienti e sezioni attive', icon: Icon.Server },
  ],
};

/**
 * Menu visibile per la sessione: il cliente vede solo le sezioni acquistate;
 * l'admin le vede tutte (quelle spente per il cliente hanno l'etichetta OFF).
 */
export function navFor(session) {
  const groups = NAV.map(g => ({
    ...g,
    items: g.items
      .filter(it => !it.feature || canUse(it.feature, session))
      .map(it => ({ ...it, off: !!it.feature && !featureEnabled(it.feature, session) })),
  })).filter(g => g.items.length);
  if (session?.canManageClients) groups.push(ADMIN_NAV);
  return groups;
}

const EXTRA_TITLES = [
  { prefix: '/circuiti/crea', kicker: 'Gestione · Circuiti', label: 'Nuovo circuito' },
  { prefix: '/circuiti/modifica', kicker: 'Gestione · Circuiti', label: 'Editor circuito' },
  { prefix: '/gare-passate/', kicker: 'Archivio · Gare', label: 'Dettaglio gara' },
];

function resolveTitle(pathname) {
  if (pathname === '/') return { kicker: 'Control center', label: 'Dashboard' };
  for (const e of EXTRA_TITLES) if (pathname.startsWith(e.prefix)) return e;
  for (const g of [...NAV, ADMIN_NAV]) for (const it of g.items) if (pathname === it.to || pathname.startsWith(it.to + '/')) return it;
  return { kicker: 'RaceSense', label: 'Live' };
}

function useServerStatus() {
  const [status, setStatus] = useState('checking');
  useEffect(() => {
    let alive = true;
    const check = () => {
      fetch(`${API_BASE}/health`).then(r => r.ok ? r.json() : Promise.reject())
        .then(j => { if (alive) setStatus(j?.ok ? 'ok' : 'down'); })
        .catch(() => { if (alive) setStatus('down'); });
    };
    check();
    const t = setInterval(check, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return status;
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

export function Brand({ compact = false }) {
  return (
    <div className={`rs-brand ${compact ? 'rs-brand--compact' : ''}`}>
      {compact
        ? <img src={logoRaceSenseMark} alt="RaceSense Live" className="rs-brand__mark" />
        : <img src={logoRaceSense} alt="RaceSense Live" className="rs-brand__logo" />}
    </div>
  );
}

export default function AppShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const status = useServerStatus();
  const now = useClock();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // La regia gara (/race) parte con la sidebar ridotta per dare spazio alla mappa.
  useEffect(() => {
    if (location.pathname === '/race') setCollapsed(true);
  }, [location.pathname]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      try { localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1'); } catch { /* noop */ }
      return !c;
    });
  };

  const title = useMemo(() => resolveTitle(location.pathname), [location.pathname]);

  // Account collegato: si aggiorna quando AuthGate rilegge i dati dal server.
  const session = useSession();
  const nav = useMemo(() => navFor(session), [session]);
  const user = session?.user;
  const inst = session?.instance;
  const links = Array.isArray(session?.links) ? session.links : [];

  const logout = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  const clock = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const date = now.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' });

  return (
    <div className={`rs-shell ${collapsed ? 'rs-shell--collapsed' : ''} ${mobileOpen ? 'rs-shell--mobile-open' : ''}`}>
      <div className="rs-shell__backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />

      <aside className="rs-sidebar" aria-label="Navigazione principale">
        <div className="rs-sidebar__brand">
          <NavLink to="/" className="rs-sidebar__brand-link" title="Dashboard">
            <Brand compact={collapsed} />
          </NavLink>
          <button type="button" className="rs-sidebar__close rs-show-mobile" onClick={() => setMobileOpen(false)} aria-label="Chiudi menu">
            <Icon.X size={18} />
          </button>
        </div>

        <nav className="rs-sidebar__nav">
          <NavLink to="/" end className={({ isActive }) => `rs-nav__item ${isActive ? 'is-active' : ''}`} title="Dashboard">
            <span className="rs-nav__icon"><Icon.Home size={17} /></span>
            <span className="rs-nav__label">Dashboard</span>
          </NavLink>
          {nav.map(group => (
            <div className="rs-nav__group" key={group.group}>
              <div className="rs-nav__group-title">{group.group}</div>
              {group.items.map(it => {
                const Ic = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    className={({ isActive }) => `rs-nav__item ${isActive ? 'is-active' : ''} ${it.accent ? `rs-nav__item--${it.accent}` : ''}`}
                    title={it.label}
                  >
                    <span className="rs-nav__icon"><Ic size={17} /></span>
                    <span className="rs-nav__label">{it.label}</span>
                    {it.off && <span className="rs-nav__off" title="Sezione non attiva per il cliente">Off</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="rs-sidebar__foot">
          <div className={`rs-server rs-server--${status}`} title={`Backend: ${API_BASE || window.location.origin}`}>
            <span className="rs-server__dot" />
            <span className="rs-server__text">
              <strong>Server</strong>
              <small>{status === 'checking' ? 'verifica…' : status === 'ok' ? 'connesso' : 'non raggiungibile'}</small>
            </span>
          </div>
          {user && (
            <div className="rs-account" title={`${user.username} · ${inst?.label || 'RaceSense'}`}>
              <span className="rs-account__avatar">{String(user.username).slice(0, 1).toUpperCase()}</span>
              <span className="rs-account__text">
                <strong>{user.username}</strong>
                <small>{inst?.label || 'RaceSense'} · {user.role === 'admin' ? 'amministratore' : 'cliente'}</small>
              </span>
            </div>
          )}
          {links.map(l => (
            <a key={l.url} className="rs-nav__item rs-nav__item--muted" href={l.url} target="_blank" rel="noopener noreferrer" title={`Apri ${l.label}`}>
              <span className="rs-nav__icon"><Icon.Share size={17} /></span>
              <span className="rs-nav__label">Apri {l.label}</span>
            </a>
          ))}
          <button type="button" className="rs-nav__item rs-nav__item--muted" onClick={logout} title="Esci">
            <span className="rs-nav__icon"><Icon.LogOut size={17} /></span>
            <span className="rs-nav__label">Esci</span>
          </button>
          <div className="rs-sidebar__meta">
            <span className="rs-sidebar__version">{APP_VERSION}</span>
            <img src={logoNexter} alt="Nexter" className="rs-sidebar__nexter" />
          </div>
          <button type="button" className="rs-sidebar__collapse rs-hide-mobile" onClick={toggleCollapsed} title={collapsed ? 'Espandi menu' : 'Riduci menu'} aria-label={collapsed ? 'Espandi menu' : 'Riduci menu'}>
            {collapsed ? <Icon.ChevronRight size={16} /> : <Icon.ChevronLeft size={16} />}
          </button>
        </div>
      </aside>

      <div className="rs-shell__main">
        <header className="rs-topbar">
          <div className="rs-topbar__left">
            <button type="button" className="rs-topbar__menu rs-show-mobile" onClick={() => setMobileOpen(true)} aria-label="Apri menu">
              <Icon.Menu size={20} />
            </button>
            <div className="rs-topbar__title">
              <span className="rs-topbar__kicker">{title.kicker}</span>
              <span className="rs-topbar__label">{title.label}</span>
            </div>
          </div>
          <div className="rs-topbar__right">
            <div className="rs-topbar__clock" aria-live="off">
              <span className="rs-topbar__date">{date}</span>
              <span className="rs-topbar__time">{clock}</span>
            </div>
            <div className={`rs-topbar__server rs-server--${status}`} title="Stato backend">
              <span className="rs-server__dot" />
              <span>{status === 'ok' ? 'ONLINE' : status === 'checking' ? '…' : 'OFFLINE'}</span>
            </div>
            {/* tema scuro/chiaro: sempre disponibile, vale per tutta la piattaforma */}
            <ThemeToggle />
          </div>
        </header>
        <main className="rs-shell__content">
          {children}
        </main>
      </div>
    </div>
  );
}
